import { put, takeLatest, select } from "redux-saga/effects";
import {
  SIGN_IN,
  GET_FACTORS,
  RESEND_PASSCODE,
  AUTHORIZE,
  SKIP_FACTORS,
  GET_QUESTION_STATUS,
  CHECK_OKTA_SESSION,
  AUTHORIZE_IN_OKTA,
  CHECK_ENCDATA_TOKEN,
  SESSION_EXPIRED,
  ENC_DATA_SIGN_IN,
} from "./constants";
import { common, get, post, SERVER_URL } from "../../httpService";
import service from "../service";
import { CHANGE_PASSWORD } from "../ChangePassword/constants";
import { UPDATE_FACTOR } from "../UpdateContact/constants";
import {
  PASSCODE_SUBMIT,
  RECOVERY_PASSCODE_SUBMIT,
  RESEND_PASSWORD_RECOVERY_CODE,
} from "../VerifyPasscode/constants";
import { VERIFY_TOKEN } from "../TokenVerification/constants";
import {
  GET_SECURITY_QUESTION,
  SET_SECURITY_QUESTION,
} from "../SetupSecurityQuestion/constants";
import { SUBMIT_ANSWER } from "../VerifySecurityQuestion/constants";
import { RESET_PASSWORD } from "../CreateNewPassword/constants";
import { oktaUrls } from "../../okta";
import { getOidcAsUrlParams } from "../../utils/oidcService";
import * as LocalStoreService from "../../LocalStoreService";
import { get as _get, isEmpty } from "lodash";
import { samlRedirect } from "./saml";
import { push } from "connected-react-router";
import { MFA_SELECT } from "../MfaFactor/constants";
import {
  MFA_RESEND_CODE,
  MFA_VERIFY_FACTOR,
} from "../VerifyMfaFactor/constants";
import {
  MFA_RESEND_CODE_FIRST_TIME,
  MFA_VERIFY_FACTOR_FIRST_TIME,
} from "../VerifyMfaFactorFirstTime/constants";

import {
  ENROLL_SPECIFIC_MFA,
  ADD_MFA_FACTOR,
} from "../EnrollSpecificMFA/constants";

const xCdkRequestIdSelector = (state) =>
  _get(state, "loginPage.xCdkRequestId", "");

function* signIn(action) {
  let api = (a) => post("/authn", a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

function* encDataSignIn(action) {
  let api = (a) => post("/authn", a.data);
  let response = yield common(action, api, true);
  if (response && response.statusCode === 401) {
    yield put(push("/login"));
  } else {
    let hasNextAction = yield service(action, response);
    if (hasNextAction) {
      yield put(hasNextAction);
    }
  }
  return response;
}

function* checkOktaSession(action) {
  let api = (a) => get(oktaUrls.SESSION, true, true, true);
  let response = yield common(action, api);
  // response = !isEmpty(response) ? response : {} // TODO: test before pushing it
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

function* authorizeInOkta() {
  let oidc = yield LocalStoreService.getOidc();
  window.location = oktaUrls.AUTHORIZE + "?" + getOidcAsUrlParams(oidc);
}

const getSessionToken = (state) =>
  _get(state, "loginPage.authnResponse.sessionToken", "");

function* authorize(action) {
  const sessionToken = yield select(getSessionToken);
  let samlParams = LocalStoreService.get("samlParams");
  samlParams = { ...samlParams, sessionToken: sessionToken };
  const queryParam = getAuthorizeParamsAsStr(action.data);
  let url = SERVER_URL + "/authorize?" + queryParam;
  if (!isEmpty(samlParams.fromURI)) {
    url = samlRedirect(samlParams);
  } else if (
    !isEmpty(samlParams.encDataLogonUrl) &&
    !samlParams.encDataLogonUrl.includes(".drive.")
  ) {
    url = samlParams.encDataLogonUrl + "?" + queryParam;
  }
  LocalStoreService.remove("samlParams");
  window.location = url;
}

function* getFactors(action) {
  const isExternal = false;
  const needHeader = true;
  const includeCredentials = false;
  let api = (a) =>
    get(
      `/api/users/${a.data.userGuid}/factors`,
      isExternal,
      needHeader,
      includeCredentials
    );
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  // console.log("hasNextAction :", hasNextAction);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

function* getQuestionStatus(action) {
  let api = (a) => get(`/api/users/${a.data.userGuid}/recoveryQuestion/status`);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  // console.log("hasNextAction :", hasNextAction);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

function* setHeaders() {
  const xCdkRequestId = yield select(xCdkRequestIdSelector);
  return { "x-cdk-request-id": xCdkRequestId };
}

export function* changePassword(action) {
  const headers = yield setHeaders();
  const isExternal = false;
  const needHeader = true;
  const includeCredentials = false;
  let api = (a) =>
    post(
      "/change-password",
      a.data,
      isExternal,
      needHeader,
      includeCredentials,
      headers
    );
  let response = yield common(action, api, true);
  if (response && response.statusCode === 401) {
    yield put({ type: SESSION_EXPIRED, message: "Session expired." });
    yield put(push("/"));
  }
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* updateFactorAction(action) {
  const headers = yield setHeaders();
  const isExternal = false;
  const needHeader = true;
  const includeCredentials = false;
  let api = (a) =>
    post(
      `/api/users/${a.userGuid}/factors`,
      a.data,
      isExternal,
      needHeader,
      includeCredentials,
      headers
    );
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

function* passcodeSubmit(action) {
  let api = (a) =>
    post(
      `/api/users/${a.userGuid}/factors/${a.factorId}/lifecycle/activate`,
      a.data
    );
  let response = yield common(action, api, true);
  if (response && response.statusCode === 200) {
    let hasNextAction = yield service(action, response);
    if (hasNextAction) {
      yield put(hasNextAction);
    }
  }
  return response;
}

function* resendPasscode(action) {
  let api = (a) =>
    post(`/api/users/${a.userGuid}/factors/${a.factorId}/resend`, a.data);
  return yield common(action, api);
}

export function* getSecurityQuestions(action) {
  let api = (a) => get(`/api/users/${a.userGuid}/factors/questions`);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* setSecurityQuestions(action) {
  let api = (a) =>
    post(
      `/api/v1/users/${a.userGuid}/credentials/change_recovery_question`,
      a.data
    );
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

const getAuthorizeParamsAsStr = (data) => {
  let user = data.user;
  let oidcParams = data.oidcParams;
  if (oidcParams) {
    let oidcParameter = getOidcAsUrlParams(oidcParams);
    let param = `${oidcParameter}&session_token=${user.sessionToken}`;
    // console.log("param :", param);
    return param;
  } else {
    let param = `session_token=${user.sessionToken}`;
    // console.log("param-no-oidc :", param);
    //TODO: Check for SSO or Federated SSO
    return param;
  }
};

export function* skipFactors(action) {
  // console.log("skipFactors");
  let hasNextAction = yield service(action, {});
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return null;
}

export function* verifyToken(action) {
  let api = (a) => post(`/api/v1/authn/recovery/token`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* submitAnswer(action) {
  let api = (a) => post(`/api/v1/authn/recovery/answer`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* savePassword(action) {
  let api = (a) => post(`/api/v1/authn/credentials/reset_password`, a.data);
  let response = yield common(action, api, true);
  if (response && response.statusCode === 401) {
    yield put({ type: SESSION_EXPIRED, message: "Session expired." });
    yield put(push("/"));
  }
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* resendRecoveryPasscode(action) {
  let api = (a) => post(`/api/v1/authn/recovery/factors/sms/resend`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* submitRecoveryPasscode(action) {
  let api = (a) => post(`/api/v1/authn/recovery/factors/sms/verify`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* checkEncDataToken(action) {
  let api = (a) => post(`/users/credentials/decrypt`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* verifyAuthnMfa(action) {
  let api = (a) =>
    post(
      `/authn/factors/${a.factorId}/verify?remember_device=${a.rememberDevice}`,
      a.data
    );
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* verifyAuthnMfaFirstTime(action) {
  let api = (a) =>
    post(`/authn/factors/${a.factorId}/lifecycle/activate`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* enrollMfaFactor(action) {
  let api = (a) => post(`/authn/factors`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* addMfaFactor(action) {
  const headers = yield setHeaders();
  const isExternal = false;
  const needHeader = true;
  const includeCredentials = false;
  let api = (a) =>
    post(
      `/api/users/${a.data.userGuid}/factors`,
      a.data.data,
      isExternal,
      needHeader,
      includeCredentials,
      headers
    );
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* resendMfaCode(action) {
  let api = (a) =>
    post(
      `/authn/factors/${a.factorId}/verify?remember_device=${a.rememberDevice}`,
      a.data
    );
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export function* resendMfaCodeFirstTime(action) {
  let api = (a) =>
    post(`/authn/factors/${a.factorId}/lifecycle/resend`, a.data);
  let response = yield common(action, api);
  let hasNextAction = yield service(action, response);
  if (hasNextAction) {
    yield put(hasNextAction);
  }
  return response;
}

export default function* sagaConfig() {
  yield takeLatest(SIGN_IN, signIn);
  yield takeLatest(ENC_DATA_SIGN_IN, encDataSignIn);
  yield takeLatest(CHECK_OKTA_SESSION, checkOktaSession);
  yield takeLatest(AUTHORIZE_IN_OKTA, authorizeInOkta);
  yield takeLatest(GET_FACTORS, getFactors);
  yield takeLatest(GET_QUESTION_STATUS, getQuestionStatus);
  yield takeLatest(MFA_SELECT, verifyAuthnMfa);

  yield takeLatest(RESEND_PASSCODE, resendPasscode);
  yield takeLatest(CHANGE_PASSWORD, changePassword);
  yield takeLatest(UPDATE_FACTOR, updateFactorAction);
  yield takeLatest(PASSCODE_SUBMIT, passcodeSubmit);
  yield takeLatest(SKIP_FACTORS, skipFactors);
  yield takeLatest(GET_SECURITY_QUESTION, getSecurityQuestions);
  yield takeLatest(SET_SECURITY_QUESTION, setSecurityQuestions);
  yield takeLatest(AUTHORIZE, authorize);
  yield takeLatest(VERIFY_TOKEN, verifyToken);
  yield takeLatest(SUBMIT_ANSWER, submitAnswer);
  yield takeLatest(RESET_PASSWORD, savePassword);
  yield takeLatest(RESEND_PASSWORD_RECOVERY_CODE, resendRecoveryPasscode);
  yield takeLatest(RECOVERY_PASSCODE_SUBMIT, submitRecoveryPasscode);
  yield takeLatest(CHECK_ENCDATA_TOKEN, checkEncDataToken);
  yield takeLatest(MFA_VERIFY_FACTOR, verifyAuthnMfa);
  yield takeLatest(MFA_RESEND_CODE, resendMfaCode);
  yield takeLatest(MFA_VERIFY_FACTOR_FIRST_TIME, verifyAuthnMfaFirstTime);
  yield takeLatest(MFA_RESEND_CODE_FIRST_TIME, resendMfaCodeFirstTime);
  yield takeLatest(ENROLL_SPECIFIC_MFA, enrollMfaFactor);
  yield takeLatest(ADD_MFA_FACTOR, addMfaFactor);
}
